import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useId, useCallback } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";
import { findPicklistOptionByValue } from "../../utils/picklistValueUtils";
import "./css/MultiSelectDropdown.css";

const MultiSelectDropdown = ({
  selectedValues = [],
  picklistValues = [],
  onChange,
  onClose,
  disabled,
  placeholder = "Select...",
  isRequired = false,
  forceOpen = false,
  /** Keys that cannot be unchecked (e.g. objects required by an enabled plan package). */
  lockedKeys = [],
}) => {
  const [show, setShow] = useState(false);
  const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues);
  const [menuPosition, setMenuPosition] = useState(null);
  const dropdownRef = useRef(null);
  const toggleRef = useRef(null);
  const menuId = useId().replace(/:/g, "");
  const lockedSet = useMemo(
    () => new Set((lockedKeys || []).map((k) => String(k))),
    [lockedKeys]
  );

  // Sync local state with prop changes
  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  // Sync show state with forceOpen prop
  useEffect(() => {
    if (forceOpen !== undefined) {
      setShow(forceOpen);
    }
  }, [forceOpen]);

  const updateMenuPosition = useCallback(() => {
    const el = toggleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!show) {
      setMenuPosition(null);
      return undefined;
    }

    updateMenuPosition();
    const rafId = requestAnimationFrame(updateMenuPosition);

    window.addEventListener("scroll", updateMenuPosition, true);
    window.addEventListener("resize", updateMenuPosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [show, updateMenuPosition, picklistValues.length]);

  const toggleValue = (key) => {
    const keyStr = String(key);
    const had = localSelectedValues.some((k) => String(k) === keyStr);
    if (had && lockedSet.has(keyStr)) {
      return;
    }
    const updated = had
      ? localSelectedValues.filter((k) => String(k) !== keyStr)
      : [...localSelectedValues, keyStr];

    setLocalSelectedValues(updated);

    if (onChange) {
      onChange(updated);
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    if (forceOpen) return;
    setShow((prev) => {
      const next = !prev;
      if (!next && onClose) {
        onClose();
      }
      return next;
    });
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (forceOpen || !show) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      const menuElement = document.getElementById(menuId);
      if (menuElement && menuElement.contains(event.target)) {
        return;
      }
      setShow(false);
      if (onClose) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [show, onClose, forceOpen, menuId]);

  const renderLabel = (key) => {
    const found = findPicklistOptionByValue(picklistValues, key);
    let base;
    if (found?.value) base = found.value;
    else if (key != null && String(key).trim()) {
      const s = String(key);
      base = s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim() || s;
    } else base = "[Unknown]";
    if (lockedSet.has(String(key))) return `${base} (required)`;
    return base;
  };

  const menuContent = picklistValues.length > 0 ? (
    picklistValues.map((opt) => {
      const optKeyStr = String(opt.key);
      const isChecked = localSelectedValues.some((k) => String(k) === optKeyStr);
      const isLocked = lockedSet.has(optKeyStr);
      const rowDisabled = disabled || isLocked;
      const labelText = isLocked ? `${opt.value} (required)` : opt.value;

      return (
        <div
          key={optKeyStr}
          className="dropdown-item-text"
          style={{
            padding: "0.5rem 1rem",
            cursor: rowDisabled ? "not-allowed" : "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled && !isLocked) {
              toggleValue(opt.key);
            }
          }}
        >
          <Form.Check
            type="checkbox"
            label={labelText}
            checked={isChecked}
            onChange={(e) => {
              e.stopPropagation();
              if (!disabled && !isLocked) {
                toggleValue(opt.key);
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            disabled={disabled || isLocked}
            className="text-dark"
          />
        </div>
      );
    })
  ) : (
    <div
      className="dropdown-item-text text-muted text-center py-2"
      style={{ cursor: "default", padding: "0.5rem 1rem" }}
    >
      <small>{disabled ? "No values available" : "No options to select"}</small>
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      className="position-relative"
      style={{ width: "100%" }}
    >
      <button
        ref={toggleRef}
        type="button"
        className="w-100 text-start form-control bg-white border d-flex justify-content-between align-items-center"
        disabled={disabled}
        aria-expanded={show}
        aria-haspopup="listbox"
        onClick={handleToggle}
        style={{
          color: disabled ? "#6c757d" : "#212529",
          fontWeight: "400",
          padding: "0.375rem 0.75rem",
          height: "calc(1.5em + 0.75rem + 2px)",
          minHeight: "38px",
          lineHeight: "1.5",
          fontSize: "1rem",
          border: "1px solid #ced4da",
          borderRadius: "0.375rem",
          backgroundColor: "#fff",
          boxShadow: "none",
        }}
      >
        <span
          className="text-truncate"
          style={{
            maxWidth: "90%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {localSelectedValues.length > 0
            ? localSelectedValues.map((key) => renderLabel(key)).join(", ")
            : placeholder}
        </span>
        <span
          className="ms-2 dropdown-caret"
          style={{ color: "#212529", fontSize: "0.8rem" }}
        >
          ▼
        </span>
      </button>

      {show &&
        menuPosition &&
        menuPosition.width > 0 &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            id={menuId}
            className="multi-select-dropdown-portal-menu"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
              minWidth: `${menuPosition.width}px`,
              maxWidth: `${menuPosition.width}px`,
            }}
          >
            {menuContent}
          </div>,
          document.body
        )}
    </div>
  );
};

export default MultiSelectDropdown;
